import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Icon,
  IconButton,
  Input,
  Spacer,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { MenuContent, MenuItem, MenuItemGroup, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar } from '@/components/ui/avatar';
import { InputGroup } from '@/components/ui/input-group';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { slotFor } from '@/lib/slot-color';
import { GroupMember, User, WorkspaceGroup, WorkspaceRoles, roleOptions } from '@/lib/types';

const MEMBERS_PER_PAGE = 40;
const CANDIDATE_CAP = 30;

const memberName = (m: GroupMember) => `${m.first_name} ${m.last_name}`.trim() || m.email;
const userName = (u: User) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email;

// Roster index letter: leading alpha of the display name, else a catch-all.
const letterOf = (m: GroupMember) => {
  const first = memberName(m).charAt(0).toUpperCase();
  return /[A-Z]/.test(first) ? first : '#';
};

interface Props {
  group: WorkspaceGroup;
  // Every group in the workspace — bulk moves can target any of the others.
  allGroups: WorkspaceGroup[];
  users: User[];
  workspaceId: string;
  onChanged: () => Promise<void>;
}

// Member management for one group. Members are selected with checkboxes and
// acted on in bulk. Semantics depend on the flavours involved:
//   - into a role-backed group  = workspace-role change (confirmed first)
//   - into a custom group       = membership row insert
//   - out of a custom group     = membership row delete (a "move" does both)
//   - out of a role-backed group only happens via the role change itself
export default function GroupMembersPanel({ group, allGroups, users, workspaceId, onChanged }: Props) {
  const roleBacked = group.auto_role_id != null;
  const ownRoleLabel =
    roleOptions.find((o) => o.value === group.auto_role_id)?.label ?? group.name;
  const roleLabelFor = (target: WorkspaceGroup) =>
    roleOptions.find((o) => o.value === target.auto_role_id)?.label ?? target.name;

  const otherGroups = allGroups.filter((g) => g.id !== group.id);
  const roleTargets = otherGroups.filter((g) => g.auto_role_id != null);
  const customTargets = otherGroups.filter((g) => g.auto_role_id == null);

  const [detail, setDetail] = useState<WorkspaceGroup | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [page, setPage] = useState(0);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [isBulkActing, setIsBulkActing] = useState(false);
  // Role-changing moves are confirmed first; this holds the chosen target.
  const [pendingRoleTarget, setPendingRoleTarget] = useState<WorkspaceGroup | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const loadDetail = useCallback(async () => {
    const res = await api.get(`groups/${group.id}`);
    setDetail(unwrap<WorkspaceGroup>(res));
  }, [group.id]);

  useEffect(() => {
    loadDetail().catch(() => {
      toaster.create({ title: 'Failed to load members', type: 'error' });
    });
  }, [loadDetail]);

  const members = useMemo(() => {
    const all = [...(detail?.members ?? [])].sort((a, b) =>
      memberName(a).localeCompare(memberName(b)),
    );
    const query = memberQuery.trim().toLowerCase();
    if (!query) return all;
    return all.filter(
      (m) =>
        memberName(m).toLowerCase().includes(query) || m.email.toLowerCase().includes(query),
    );
  }, [detail, memberQuery]);

  const pageCount = Math.max(1, Math.ceil(members.length / MEMBERS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageMembers = members.slice(
    safePage * MEMBERS_PER_PAGE,
    (safePage + 1) * MEMBERS_PER_PAGE,
  );

  // Checked ids narrowed to people who are still members.
  const checkedIds = useMemo(() => {
    const memberIds = new Set((detail?.members ?? []).map((m) => m.user_id));
    return [...checked].filter((id) => memberIds.has(id));
  }, [checked, detail]);

  const allFilteredChecked = members.length > 0 && members.every((m) => checked.has(m.user_id));
  const someFilteredChecked = members.some((m) => checked.has(m.user_id));

  const toggleMember = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select/clear everyone matching the current search, not just this page.
  const toggleAllFiltered = () => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allFilteredChecked) members.forEach((m) => next.delete(m.user_id));
      else members.forEach((m) => next.add(m.user_id));
      return next;
    });
  };

  // Who can be added: active workspace users not already in the group. For
  // role-backed groups only coaches/viewers qualify — admins are never
  // role-changed from here.
  const candidates = useMemo(() => {
    const memberIds = new Set((detail?.members ?? []).map((m) => m.user_id));
    const query = addQuery.trim().toLowerCase();
    return users
      .filter((u) => u.is_active && !memberIds.has(u.id))
      .filter(
        (u) =>
          !roleBacked ||
          ((u.role_id === WorkspaceRoles.WorkspaceUser ||
            u.role_id === WorkspaceRoles.WorkspaceViewer) &&
            u.role_id !== group.auto_role_id),
      )
      .filter(
        (u) =>
          !query ||
          userName(u).toLowerCase().includes(query) ||
          (u.email ?? '').toLowerCase().includes(query),
      )
      .sort((a, b) => userName(a).localeCompare(userName(b)));
  }, [users, detail, addQuery, roleBacked, group.auto_role_id]);
  const shownCandidates = candidates.slice(0, CANDIDATE_CAP);

  const toggleSelected = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const changeRole = (userId: number, roleId: number) =>
    api.post('/users/role/workspace/change', {
      user_id: userId,
      role_id: roleId,
      workspace_id: Number(workspaceId),
    });

  // Returns the ids whose role change succeeded.
  const bulkChangeRole = async (ids: number[], roleId: number) => {
    const results = await Promise.allSettled(ids.map((id) => changeRole(id, roleId)));
    return ids.filter((_, i) => results[i].status === 'fulfilled');
  };

  // Returns the ids whose removal succeeded.
  const bulkRemoveFromThisGroup = async (ids: number[]) => {
    const results = await Promise.allSettled(
      ids.map((id) => api.delete(`groups/${group.id}/members/${id}`)),
    );
    return ids.filter((_, i) => results[i].status === 'fulfilled');
  };

  const finishBulk = async (okCount: number, total: number, successTitle: string) => {
    if (okCount < total) {
      toaster.create({ title: `${total - okCount} of ${total} failed`, type: 'error' });
    } else {
      toaster.create({ title: successTitle, type: 'success' });
    }
    setChecked(new Set());
    await Promise.all([loadDetail(), onChanged()]);
  };

  const handleBulkRemove = async () => {
    if (checkedIds.length === 0) return;
    setIsBulkActing(true);
    try {
      const ok = await bulkRemoveFromThisGroup(checkedIds);
      await finishBulk(
        ok.length,
        checkedIds.length,
        `Removed ${ok.length} from ${group.name}`,
      );
    } finally {
      setIsBulkActing(false);
    }
  };

  // Custom-group target: add the selection there; when this group is custom
  // too, it's a move, so they leave here as well. Duplicate adds are ignored
  // server-side.
  const handleMoveToCustom = async (target: WorkspaceGroup) => {
    if (checkedIds.length === 0) return;
    setIsBulkActing(true);
    try {
      await api.post(`groups/${target.id}/members`, { user_ids: checkedIds });
      if (roleBacked) {
        await finishBulk(
          checkedIds.length,
          checkedIds.length,
          `Added ${checkedIds.length} to ${target.name}`,
        );
      } else {
        const ok = await bulkRemoveFromThisGroup(checkedIds);
        await finishBulk(ok.length, checkedIds.length, `Moved ${ok.length} to ${target.name}`);
      }
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || `Failed to add to ${target.name}`,
        type: 'error',
      });
    } finally {
      setIsBulkActing(false);
    }
  };

  // Role-backed target, after the confirm dialog: change roles; when this
  // group is custom the selection also leaves it (it's a move).
  const handleRoleMove = async () => {
    const target = pendingRoleTarget;
    const roleId = target?.auto_role_id;
    if (!target || roleId == null || checkedIds.length === 0) return;
    setIsBulkActing(true);
    try {
      const ok = await bulkChangeRole(checkedIds, roleId);
      if (!roleBacked && ok.length > 0) {
        await bulkRemoveFromThisGroup(ok);
      }
      setPendingRoleTarget(null);
      await finishBulk(ok.length, checkedIds.length, `Moved ${ok.length} to ${target.name}`);
    } finally {
      setIsBulkActing(false);
    }
  };

  const handleAdd = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setIsAdding(true);
    try {
      if (roleBacked) {
        const roleId = group.auto_role_id;
        if (roleId == null) return;
        const ok = await bulkChangeRole(ids, roleId);
        if (ok.length < ids.length) {
          toaster.create({
            title: `${ids.length - ok.length} of ${ids.length} role changes failed`,
            type: 'error',
          });
        } else {
          toaster.create({
            title: ids.length === 1 ? '1 person moved' : `${ids.length} people moved`,
            type: 'success',
          });
        }
      } else {
        await api.post(`groups/${group.id}/members`, { user_ids: ids });
        toaster.create({
          title: ids.length === 1 ? 'Member added' : `${ids.length} members added`,
          type: 'success',
        });
      }
      setSelected(new Set());
      setAddQuery('');
      setAddOpen(false);
      await Promise.all([loadDetail(), onChanged()]);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to add members',
        type: 'error',
      });
    } finally {
      setIsAdding(false);
    }
  };

  if (!detail) {
    return (
      <Center py={6}>
        <Spinner size='sm' />
      </Center>
    );
  }

  return (
    <Box>
      {roleBacked && (
        <Text fontSize='xs' color='fg.muted' mb={3}>
          {`Membership follows the ${ownRoleLabel} role — moving people into or out of this group changes their workspace role.`}
        </Text>
      )}

      <Flex gap={3} mb={3} align='center' wrap='wrap'>
        <InputGroup
          maxW='280px'
          w='full'
          startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='fg.subtle' />}
        >
          <Input
            size='sm'
            bg='bg.panel'
            placeholder={`Search ${detail.members?.length ?? 0} members…`}
            value={memberQuery}
            onChange={(e) => {
              setMemberQuery(e.target.value);
              setPage(0);
            }}
          />
        </InputGroup>
        {members.length > 0 && (
          <Checkbox
            size='sm'
            checked={allFilteredChecked ? true : someFilteredChecked ? 'indeterminate' : false}
            onCheckedChange={toggleAllFiltered}
          >
            <Text as='span' fontSize='xs' color='fg.muted' whiteSpace='nowrap'>
              {memberQuery.trim()
                ? `Select ${members.length} matching`
                : `Select all ${members.length}`}
            </Text>
          </Checkbox>
        )}
        <Spacer />
        <Button
          size='xs'
          variant={addOpen ? 'outline' : 'primary'}
          onClick={() => {
            setAddOpen((v) => !v);
            setAddQuery('');
            setSelected(new Set());
          }}
        >
          <Icon as={PlusIcon} w={3.5} h={3.5} />
          Add people
        </Button>
      </Flex>

      {checkedIds.length > 0 && (
        <Flex
          align='center'
          gap={2}
          mb={3}
          py={2}
          px={3}
          bg='brand.soft'
          borderWidth='1px'
          borderColor='brand.emphasized'
          borderRadius='md'
          shadow='sm'
          position='sticky'
          top='76px'
          zIndex={5}
          wrap='wrap'
        >
          <Text fontSize='sm' fontWeight='600' color='fg.strong'>
            {checkedIds.length} selected
          </Text>
          <Button size='xs' variant='ghost' onClick={() => setChecked(new Set())}>
            Clear
          </Button>
          <Spacer />
          {otherGroups.length > 0 && (
            <MenuRoot>
              <MenuTrigger asChild>
                <Button size='xs' variant='outline' bg='bg.panel' loading={isBulkActing}>
                  {roleBacked ? 'Move or add to…' : 'Move to…'}
                  <Icon as={ChevronDownIcon} w={3.5} h={3.5} />
                </Button>
              </MenuTrigger>
              <MenuContent>
                {roleTargets.length > 0 && (
                  <MenuItemGroup
                    title={
                      roleBacked
                        ? 'Move — changes their workspace role'
                        : 'Move — changes their workspace role, leaves this group'
                    }
                  >
                    {roleTargets.map((target) => (
                      <MenuItem
                        key={target.id}
                        value={`role-${target.id}`}
                        onClick={() => setPendingRoleTarget(target)}
                      >
                        {target.name}
                      </MenuItem>
                    ))}
                  </MenuItemGroup>
                )}
                {customTargets.length > 0 && (
                  <MenuItemGroup
                    title={
                      roleBacked
                        ? `Add — they stay in ${group.name}`
                        : 'Move — leaves this group'
                    }
                  >
                    {customTargets.map((target) => (
                      <MenuItem
                        key={target.id}
                        value={`custom-${target.id}`}
                        onClick={() => handleMoveToCustom(target)}
                      >
                        {target.name}
                      </MenuItem>
                    ))}
                  </MenuItemGroup>
                )}
              </MenuContent>
            </MenuRoot>
          )}
          {!roleBacked && (
            <Button
              size='xs'
              variant='outline'
              bg='bg.panel'
              color='danger'
              loading={isBulkActing}
              onClick={handleBulkRemove}
            >
              Remove from group
            </Button>
          )}
        </Flex>
      )}

      {addOpen && (
        <Box borderWidth='1px' borderColor='border' borderRadius='md' bg='bg.panel' shadow='sm' p={3} mb={4}>
          <Text fontSize='xs' color='fg.muted' mb={2}>
            {roleBacked
              ? `Anyone you add becomes ${ownRoleLabel} — their workspace role changes.`
              : 'Pick people from your workspace roster.'}
          </Text>
          <Input
            size='sm'
            maxW='sm'
            autoFocus
            bg='bg.panel'
            placeholder='Search people…'
            value={addQuery}
            onChange={(e) => setAddQuery(e.target.value)}
          />
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} columnGap={4} mt={2}>
            {shownCandidates.map((user) => (
              <Checkbox
                key={user.id}
                size='sm'
                px={2}
                py={1}
                mx={-2}
                borderRadius='sm'
                _hover={{ bg: 'bg.subtle' }}
                checked={selected.has(user.id)}
                onCheckedChange={() => toggleSelected(user.id)}
              >
                <Text as='span' fontSize='sm' color='fg.strong'>
                  {userName(user)}
                </Text>{' '}
                <Text as='span' fontSize='xs' color='fg.muted'>
                  {user.email}
                </Text>
              </Checkbox>
            ))}
          </Grid>
          {candidates.length === 0 && (
            <Text fontSize='xs' color='fg.muted' mt={1}>
              Nobody matches.
            </Text>
          )}
          {candidates.length > CANDIDATE_CAP && (
            <Text fontSize='xs' color='fg.muted' mt={1}>
              {candidates.length - CANDIDATE_CAP} more — refine your search.
            </Text>
          )}
          <Flex mt={3} gap={2}>
            <Spacer />
            <Button
              size='xs'
              variant='ghost'
              onClick={() => {
                setAddOpen(false);
                setSelected(new Set());
              }}
            >
              Cancel
            </Button>
            <Button
              size='xs'
              variant='primary'
              disabled={selected.size === 0}
              loading={isAdding}
              onClick={handleAdd}
            >
              {selected.size === 0
                ? 'Select people'
                : roleBacked
                  ? `Move ${selected.size} into ${group.name}`
                  : `Add ${selected.size} to group`}
            </Button>
          </Flex>
        </Box>
      )}

      {members.length === 0 ? (
        <Flex direction='column' align='center' gap={2} py={10}>
          <Center w='40px' h='40px' borderRadius='full' bg='bg.panel' borderWidth='1px' borderColor='border'>
            <Icon as={UserGroupIcon} w={5} h={5} color='fg.subtle' />
          </Center>
          <Text fontSize='sm' color='fg.muted'>
            {memberQuery
              ? 'No members match your search.'
              : 'No members yet — add people to build this audience.'}
          </Text>
        </Flex>
      ) : (
        <>
          <Box
            bg='bg.panel'
            borderWidth='1px'
            borderColor='border'
            borderRadius='md'
            shadow='sm'
            px={4}
            pt={1}
            pb={2}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} columnGap={10}>
              {(() => {
                const showLetters = members.length > 12;
                const rows: ReactNode[] = [];
                let lastLetter = '';
                for (const member of pageMembers) {
                  const letter = letterOf(member);
                  if (showLetters && letter !== lastLetter) {
                    lastLetter = letter;
                    rows.push(
                      <Flex
                        key={`letter-${letter}`}
                        gridColumn='1 / -1'
                        align='center'
                        gap={3}
                        pt={3}
                        pb={1}
                      >
                        <Center
                          w='20px'
                          h='20px'
                          borderRadius='sm'
                          bg={`slot${slotFor(letter)}.bg`}
                        >
                          <Text
                            fontSize='2xs'
                            fontWeight='700'
                            color={`slot${slotFor(letter)}.fg`}
                          >
                            {letter}
                          </Text>
                        </Center>
                        <Box flex='1' h='1px' bg='border.subtle' />
                      </Flex>,
                    );
                  }
                  rows.push(
                    <Checkbox
                      key={member.user_id}
                      size='sm'
                      w='full'
                      px={2}
                      py={1.5}
                      mx={-2}
                      borderRadius='sm'
                      _hover={{ bg: 'bg.subtle' }}
                      checked={checked.has(member.user_id)}
                      onCheckedChange={() => toggleMember(member.user_id)}
                    >
                      <Flex align='center' gap={2.5} minW={0}>
                        <Avatar
                          name={memberName(member)}
                          size='xs'
                          bg={`slot${slotFor(letterOf(member))}.bg`}
                          color={`slot${slotFor(letterOf(member))}.fg`}
                          flexShrink={0}
                        />
                        <Box minW={0}>
                          <Text fontSize='sm' color='fg.strong' truncate>
                            {memberName(member)}
                          </Text>
                          <Text fontSize='xs' color='fg.muted' truncate>
                            {member.email}
                          </Text>
                        </Box>
                      </Flex>
                    </Checkbox>,
                  );
                }
                return rows;
              })()}
            </Grid>
          </Box>
          {pageCount > 1 && (
            <Flex align='center' justify='center' gap={3} mt={3}>
              <IconButton
                aria-label='Previous page'
                size='xs'
                variant='ghost'
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                <Icon as={ChevronLeftIcon} w={3.5} h={3.5} />
              </IconButton>
              <Text fontSize='xs' color='fg.muted'>
                {safePage * MEMBERS_PER_PAGE + 1}–
                {Math.min(members.length, (safePage + 1) * MEMBERS_PER_PAGE)} of {members.length}
              </Text>
              <IconButton
                aria-label='Next page'
                size='xs'
                variant='ghost'
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                <Icon as={ChevronRightIcon} w={3.5} h={3.5} />
              </IconButton>
            </Flex>
          )}
        </>
      )}

      <DialogRoot
        lazyMount
        open={pendingRoleTarget != null}
        onOpenChange={() => setPendingRoleTarget(null)}
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to {pendingRoleTarget?.name}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text fontSize='sm'>
              {checkedIds.length === 1 ? '1 person becomes' : `${checkedIds.length} people become`}{' '}
              {pendingRoleTarget ? roleLabelFor(pendingRoleTarget) : ''} — their workspace role
              changes, which updates what they can do across the workspace.
              {!roleBacked && ` They also leave ${group.name}.`}
            </Text>
          </DialogBody>
          <DialogFooter>
            <Button variant='ghost' size='sm' onClick={() => setPendingRoleTarget(null)}>
              Cancel
            </Button>
            <Button variant='primary' size='sm' loading={isBulkActing} onClick={handleRoleMove}>
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
}
